/**
 * §8.2: "The briefing must show a usable prompt — add your first task, or run the seed
 * import — not a blank page. An empty dashboard on day one is how this gets abandoned on
 * day two."
 *
 * Self-contained like every other portable card (D118) — its own fetch, this time of
 * `GET /api/briefing`, the one monolithic endpoint left (D104-107 split everything else off
 * it, but nothing else needed `empty_state` badly enough to earn its own route). `no_tasks`
 * and `no_areas` there are plain `COUNT(*)` checks, independent of the energy filter the rest
 * of that payload is scoped by — the same "whole table, not just what's visible" `no_tasks`
 * `TasksCard` reads off `/api/briefing/open`.
 *
 * Renders `LayoutGrid`'s own `Placeholder` while unlocked and there's nothing to show, for
 * the same reason `NoticeCard` does — `renderSection` produces a real `<FirstRunCard/>`
 * element the instant "first-run" is registered, so `LayoutGrid` never sees a gap to fill on
 * its own; only this component knows whether it has anything to say.
 */

import { useEffect, useState } from "react";
import { ApiError, api } from "../api";
import { useEditMode } from "../layout/editModeContext";
import { Placeholder } from "../layout/LayoutGrid";
import { Link } from "../router";

export function FirstRunCard({ refreshKey }: { refreshKey?: number }) {
  const [state, setState] = useState<{ noTasks: boolean; noAreas: boolean } | null>(null);
  const { editing } = useEditMode();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const body = await api.briefing();
        if (!cancelled) {
          setState({ noTasks: body.empty_state.no_tasks, noAreas: body.empty_state.no_areas });
        }
      } catch (err) {
        if (err instanceof ApiError && err.isAuth) return; // AuthGate takes over
        // Nothing to show on a failed fetch here — the tasks/areas cards elsewhere on the
        // page already surface the same error.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  if (!state?.noTasks) return editing ? <Placeholder id="first-run" /> : null;

  return (
    <section className="panel">
      <div className="empty">
        <strong>Nothing here yet - that's expected.</strong>
        <ol>
          {state.noAreas && (
            <li>
              Create an area, or seed them from the vault:{" "}
              <code>python -m efss.seed areas --apply</code>
            </li>
          )}
          <li>Type a task in the box above and press enter. That is the whole flow.</li>
          <li>
            Want something to look at first? <code>python -m efss.seed demo</code> fills this
            with invented data.
          </li>
          <li>
            Or go straight to <Link to="/blackjack">blackjack</Link> and let a hand pick what
            you do.
          </li>
        </ol>
      </div>
    </section>
  );
}
